import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../Icon";
import { useIsMobile } from "./useIsMobile";

const SIZES = {
  sm: 360,
  md: 480,
  lg: 640,
  xl: 820,
};

const OPEN_MS = 280;
const CLOSE_MS = 220;

/**
 * Drawer DS — panel deslizante desde la derecha, portal nativo, backdrop,
 * cierre con ESC y click en backdrop. Sin reactstrap.
 *
 * Uso: <Drawer open onClose title="Detalle">{content}</Drawer>
 *      <Drawer open onClose title={<Header/>} footer={<Footer/>} size="lg">…
 */
export function Drawer({
  open,
  onClose,
  title,
  size = "md",
  children,
  footer,
  closeOnBackdrop = true,
  className,
}) {
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);
  const [mounted, setMounted] = useState(open);
  const [active, setActive] = useState(false);

  // Guardamos onClose en una ref para que el effect de foco/ESC no se
  // re-ejecute cada vez que el padre re-renderiza (los padres pasan
  // arrow functions inline). Sin esto, cada keystroke en un input
  // disparaba el effect → reasigna foco → ladrón de foco al botón X.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      let r2 = 0;
      const r1 = requestAnimationFrame(() => {
        r2 = requestAnimationFrame(() => setActive(true));
      });
      return () => {
        cancelAnimationFrame(r1);
        if (r2) cancelAnimationFrame(r2);
      };
    }
    setActive(false);
    const t = setTimeout(() => setMounted(false), CLOSE_MS);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!mounted) return undefined;
    previouslyFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      if (!dialogRef.current) return;
      // Excluimos el botón X (data-drawer-close) para que el foco inicial
      // recaiga en el primer input/textarea/select útil del formulario.
      const focusable = dialogRef.current.querySelector(
        'input:not([type="hidden"]),select,textarea,[tabindex]:not([tabindex="-1"]),button:not([data-drawer-close])',
      );
      focusable?.focus();
    }, 50);
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [mounted]);

  const isMobile = useIsMobile();
  if (!mounted) return null;
  // En mobile el drawer ocupa el viewport completo — el `size` se ignora.
  const width = isMobile ? "100%" : (SIZES[size] ?? SIZES.md);
  const bodyPad = isMobile ? 16 : 20;
  const headerPad = isMobile ? "12px 16px" : "16px 20px";
  const footerPad = isMobile ? "12px 16px" : "12px 20px";

  return createPortal(
    <div
      role="presentation"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,19,22,0.45)",
        zIndex: 1080,
        display: "flex",
        justifyContent: "flex-end",
        opacity: active ? 1 : 0,
        transition: `opacity ${active ? OPEN_MS : CLOSE_MS}ms var(--kr-ease-out)`,
      }}
    >
      <aside
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        className={className}
        style={{
          width: "100%",
          maxWidth: width,
          background: "var(--kr-bg-surface)",
          borderLeft: isMobile ? "none" : "1px solid var(--kr-border)",
          boxShadow: "var(--kr-shadow-xl)",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          transform: active ? "translateX(0)" : "translateX(100%)",
          transition: active
            ? `transform ${OPEN_MS}ms var(--kr-ease-out)`
            : `transform ${CLOSE_MS}ms var(--kr-ease-in)`,
          willChange: "transform",
        }}
      >
        {title != null && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: headerPad,
              borderBottom: "1px solid var(--kr-border-subtle)",
              flexShrink: 0,
            }}
          >
            {typeof title === "string" ? (
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--kr-fg-strong)",
                }}
              >
                {title}
              </span>
            ) : (
              title
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              data-drawer-close="true"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "var(--kr-fg-muted)",
                // 36px tap target en mobile (≥ Apple HIG 44 ideal pero 36
                // funciona con icono 16). En desktop mantenemos el 24px
                // original para que la cabecera no se vea pesada.
                padding: isMobile ? 8 : 4,
                borderRadius: 6,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--kr-bg-subtle)";
                e.currentTarget.style.color = "var(--kr-fg-strong)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--kr-fg-muted)";
              }}
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: bodyPad,
          }}
        >
          {children}
        </div>
        {footer && (
          <div
            style={{
              padding: footerPad,
              borderTop: "1px solid var(--kr-border-subtle)",
              display: "flex",
              gap: 8,
              // En mobile el footer puede traer 3+ botones — se permite
              // wrap para que no se aplaste. En desktop seguimos con
              // alineación a la derecha sin wrap.
              flexWrap: isMobile ? "wrap" : "nowrap",
              justifyContent: "flex-end",
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        )}
      </aside>
    </div>,
    document.body,
  );
}

export default Drawer;
